// Console shell — two nav chromes over the same routes:
//   below lg: bottom tab bar (live operators use it daily)
//   lg+:      left icon rail + sticky top bar (date, desk state, ⌘K hint) and
//             the command palette, in the desktop console style. Content sits
//             in a full-canvas column (rail-offset, max 1600px, centered).
// Screens render unchanged inside <main>; only the chrome swaps.
//
// TWO GROUPS, NOT ONE LIST (operator, 7 Sept 2026: "look at the clutter…
// hide screens which are not daily useful"). DAILY is the seven screens the desk
// opens every day and is always on show. MORE is the reports and windows —
// behind one collapsed group on the rail (its open/closed state is remembered)
// and behind one "More" item on the phone. Nothing is removed from the app:
// every screen keeps its route and its ⌘K line.
//
// Names (/attach-names) left the nav entirely: it is the legacy name-attachment
// tool, empties as names get attached, and is not a daily screen. Route and
// palette entry stay.

import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, LayoutGrid } from 'lucide-react';
import { TopBar } from './TopBar';
import { CommandPalette } from './CommandPalette';
import { useIsDesktop } from '../lib/useIsDesktop';
import { useNewerBuild } from '../lib/useNewerBuild';
import { navRoutes, inMoreGroup, type RouteEntry } from '../lib/routes';
import { uiV2 } from '../lib/ui-v2';
import { ShellV2 } from './v2/ShellV2';
import { NewConsoleDoor } from './v2/NewConsoleDoor';

// THE ONE LABEL TABLE (sweep item 71, 14 Sept 2026): every word and icon here
// comes from lib/routes.ts, the same rows the top bar, the ⌘K palette and
// Home's action row read. This file only decides where each group sits.
//
// DAILY — always shown, in the order a day runs. MORE — the reports and
// windows, behind the fold. ACTIONS — pinned to the bottom of the rail; never
// scroll, never fold. Convert joined the doors on 12 Sept 2026 (evening) — the
// operator asked for one screen to convert any balance, party or desk
// account, from the left side.
const DAILY: RouteEntry[] = navRoutes('daily');
const MORE: RouteEntry[] = navRoutes('more');
const ACTIONS: RouteEntry[] = navRoutes('action');

// The rail's MORE group remembers whether it was left open. Same idiom as
// Treasury's folded groups: a preference, read and written inside try/catch,
// because private mode or a locked-down webview must never break the nav.
const MORE_KEY = 'console.nav.more';
function useMoreOpen(): [boolean, (v: boolean) => void] {
  const [open, setOpen] = useState<boolean>(() => {
    try { return localStorage.getItem(MORE_KEY) === '1'; } catch { return false; }
  });
  const set = (v: boolean) => {
    setOpen(v);
    try { localStorage.setItem(MORE_KEY, v ? '1' : '0'); } catch { /* preference only */ }
  };
  return [open, set];
}

function RailLink({ path, label, title, icon: Icon, end }: RouteEntry) {
  return (
    <NavLink
      to={path}
      end={end ?? false}
      title={title}
      className={({ isActive }) => `console-nav-btn ${isActive ? 'console-nav-btn-active' : ''}`}
    >
      {Icon && <Icon className="w-[18px] h-[18px]" />}
      <span>{label}</span>
    </NavLink>
  );
}

/** THE REBUILD'S SWITCH (lib/ui-v2.ts): batch 1 on draws the rebuilt shell; off, this file's own chrome, as on main. */
export function ConsoleShell({ children }: { children: ReactNode }) {
  return uiV2(1) ? <ShellV2>{children}</ShellV2> : <LegacyShell>{children}</LegacyShell>;
}

function LegacyShell({ children }: { children: ReactNode }) {
  // Desktop-only console chrome. Mounted strictly at lg+ so below lg the
  // DOM (and behavior — no global key listeners) is identical to before.
  const isDesktop = useIsDesktop();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useMoreOpen();
  const { pathname, search } = useLocation();
  const onMore = inMoreGroup(pathname);

  // The phone's More sheet is a moment, not a preference: it closes on every
  // navigation and never persists.
  const [sheetOpen, setSheetOpen] = useState(false);
  useEffect(() => { setSheetOpen(false); }, [pathname]);

  // A NEWER CONSOLE IS READY (14 Sept 2026, lib/useNewerBuild.ts). One slim
  // bar above the content with a Reload button — and on the NEXT route change
  // while it is up, the tab reloads by itself onto the new path, so the
  // operator never has to think about it. Never mid-screen: a route change is
  // the one moment no form is being typed into. The path the effect saw on
  // its first run is the one the bar appeared on; any other path is a change.
  // The PATH decides, never the query: the desk switch and the Convert list
  // rewrite ?entity= / ?party= in place while a form is being typed into,
  // and a reload there would have eaten the operator's input (second
  // review, 14 Sept 2026).
  const newer = useNewerBuild();
  const seenAt = useRef<string | null>(null);
  useEffect(() => {
    if (!newer) return;
    if (seenAt.current === null) { seenAt.current = pathname; return; }
    if (pathname !== seenAt.current) window.location.assign(`${pathname}${search}`);
  }, [newer, pathname]);   // eslint-disable-line react-hooks/exhaustive-deps

  // THE PHONE FRAME (19 Sept 2026, the keyboard): below lg the column is a
  // box exactly the visible height (--app-height, lib/viewport.ts) and
  // <main> scrolls inside it; the document does not scroll (styles.css).
  // The bar stays fixed at the bottom: on a keyboard that resizes the web
  // view it sits above the keys, on one that overlays it sits behind them —
  // never mid-screen. At lg+ the column is the page, as it always was.
  return (
    <div className="min-h-screen flex flex-col max-lg:min-h-0 max-lg:h-[var(--app-height,100dvh)] max-lg:overflow-hidden">
      {/* Desktop rail (lg+) */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 z-30 w-[72px] flex-col items-center gap-1 py-3.5 bg-panel border-r border-stroke">
        <div className="w-10 h-10 mb-3.5 rounded-lg bg-gradient-to-br from-tg-accent to-brand-gold-dim grid place-items-center text-title font-extrabold text-tg-button-text tracking-tight select-none">
          D
        </div>
        {/* The NAV scrolls; the three ACTIONS never do. `min-h-0` is
            load-bearing: a flex child will not shrink below its content height
            without it, so the list would overflow instead of scrolling — and
            on a short window Record used to be cut off below the screen edge. */}
        <div className="flex-1 min-h-0 w-full flex flex-col items-center gap-1 overflow-y-auto overscroll-contain">
          {DAILY.map((item) => <RailLink key={item.path} {...item} />)}

          {/* The fold. Lit like an active item when the operator is on a MORE
              screen with the group closed, so the rail never shows nothing
              selected — without touching the remembered preference. */}
          <button
            type="button"
            aria-expanded={moreOpen}
            title={moreOpen ? 'Fold the reports away' : 'Reports and windows'}
            onClick={() => setMoreOpen(!moreOpen)}
            className={`console-nav-btn ${!moreOpen && onMore ? 'console-nav-btn-active' : ''}`}
          >
            <ChevronDown className={`w-[18px] h-[18px] transition-transform ${moreOpen ? 'rotate-180' : ''}`} />
            <span>More</span>
          </button>
          {moreOpen && MORE.map((item) => <RailLink key={item.path} {...item} />)}
        </div>
        <div className="w-full shrink-0 flex flex-col items-center gap-1 pt-2 mt-1 border-t border-stroke-soft">
        {ACTIONS.map(({ path, label, title, icon: Icon }) => (
          <NavLink
            key={path}
            to={path}
            title={title}
            className={({ isActive }) =>
              `console-nav-btn ${isActive ? 'console-nav-btn-active' : ''}`
            }
          >
            {Icon && <Icon className="w-[18px] h-[18px]" />}
            <span>{label}</span>
          </NavLink>
        ))}
        </div>
      </aside>

      {/* Desktop top bar + command palette (lg+ only; nothing below lg) */}
      {isDesktop && <TopBar onOpenPalette={() => setPaletteOpen(true)} />}
      {isDesktop && <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />}

      {/* Full-canvas content column: full width minus the rail, capped at
          1440px (down from 1600 on 17 Sept 2026 — the operator's "blank
          space is too much" on a wide monitor) and centered — mobile padding
          classes untouched. */}
      <main className="flex-1 min-h-0 max-lg:overflow-y-auto max-lg:overscroll-contain p-3 pb-24 lg:ml-[72px] lg:px-6 lg:pt-6 lg:pb-16">
        <div className="lg:max-w-[1440px] lg:mx-auto">
          {newer && (
            <div className="mb-3 flex items-center justify-between gap-3 rounded-lg border border-tg-accent/40 bg-tg-accent/10 px-3 py-1.5 text-meta text-tg-text">
              <span>A newer console is ready</span>
              <button type="button" className="btn-link text-meta font-medium" onClick={() => window.location.reload()}>
                Reload
              </button>
            </div>
          )}
          {children}
        </div>
      </main>

      {/* Phone — backdrop for the More sheet. A tap anywhere outside folds it. */}
      {sheetOpen && (
        <div
          className="lg:hidden fixed inset-0 z-20 bg-black/40"
          onClick={() => setSheetOpen(false)}
          aria-hidden
        />
      )}

      {/* Phone — the same split as the rail: DAILY as tabs, MORE behind one
          "More" item that opens a sheet above the bar. Seven tabs and More
          share the width evenly with no side padding — at 360px that is 45px a
          tab, and "Treasury" in the label role (11px, tracking-tight) is 43 —
          and a label truncates before the bar ever wraps. The role is `label`,
          not `meta` (22 Oct 2026): at 12px the word is 49px and the codemod's
          first pass drew "Treasu…" at 390 and "Treas…" at 360 on the phone's
          main nav; at 11px alone it is 45, a pixel's rounding from cut.
          The book / move / record actions ride at the top of the sheet so the
          phone loses no door it had. */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-30">
        {sheetOpen && (
          <div className="rounded-t-xl border-t border-tg-secondary bg-tg-section-bg px-3 pt-3 pb-2 space-y-3">
            {/* Two by two since Convert made it four (12 Sept 2026): three
                across left one action alone on a row of its own. */}
            <div className="grid grid-cols-2 gap-2">
              {ACTIONS.map(({ path, title, icon: Icon }) => (
                <NavLink
                  key={path}
                  to={path}
                  className="btn-secondary text-center text-meta py-2 flex items-center justify-center gap-1.5"
                >
                  {Icon && <Icon className="w-4 h-4" />}
                  {title}
                </NavLink>
              ))}
            </div>
            <div className="grid grid-cols-4 gap-1">
              {MORE.map(({ path, label, icon: Icon, end }) => (
                <NavLink
                  key={path}
                  to={path}
                  end={end ?? false}
                  className={({ isActive }) =>
                    `flex flex-col items-center gap-1 rounded-lg px-1 py-2 text-meta ${isActive ? 'text-tg-accent bg-tg-accent/10' : 'text-tg-hint'}`
                  }
                >
                  {Icon && <Icon className="w-5 h-5" />}
                  <span className="max-w-full truncate">{label}</span>
                </NavLink>
              ))}
            </div>
            {/* The owner's door to the new console (UI rebuild); nothing for anyone else. */}
            <NewConsoleDoor className="block py-2 text-center" />
          </div>
        )}
        <nav className="bg-tg-section-bg border-t border-tg-secondary flex py-2">
          {DAILY.map(({ path, label, icon: Icon, end }) => (
            <NavLink
              key={path}
              to={path}
              end={end ?? false}
              className={({ isActive }) =>
                `flex flex-1 min-w-0 flex-col items-center text-label tracking-tight px-0 py-1 whitespace-nowrap ${isActive && !sheetOpen ? 'text-tg-accent' : 'text-tg-hint'}`
              }
            >
              {Icon && <Icon className="w-5 h-5 mb-0.5" />}
              <span className="max-w-full truncate">{label}</span>
            </NavLink>
          ))}
          <button
            type="button"
            aria-expanded={sheetOpen}
            onClick={() => setSheetOpen((v) => !v)}
            className={`flex flex-1 min-w-0 flex-col items-center text-label tracking-tight px-0 py-1 whitespace-nowrap ${sheetOpen || onMore ? 'text-tg-accent' : 'text-tg-hint'}`}
          >
            <LayoutGrid className="w-5 h-5 mb-0.5" />
            <span>More</span>
          </button>
        </nav>
      </div>
    </div>
  );
}
